import { type ChangeEventHandler, type CSSProperties, useCallback, useMemo } from 'react';

import classnames from 'classnames';

import { AdditionalFields } from '@/shared/additionalFields';

import { NewsletterListPicker } from './components/NewsletterListPicker';
import SignupInput from './components/SignupInput';

import { computeFont } from '../utils/fonts';
import { getCheckboxRadius } from '../utils/getCheckboxRadius';
import { isRenderableSignupIcon } from './utils/isRenderableSignupIcon';

import { Icon } from '../icon';
import type { TIcon } from '../types';
import * as styles from './Signup.css';
import type { SignupElementProps } from './Signup.types';
import { SignupGDPRCompliance } from './SignupGDPRCompliance';

function signupSubmitButtonIcon(icon: TIcon) {
  if (icon.type === 'icon') {
    return <Icon type={icon.iconType} name={icon.iconName} size={20} />;
  }
  return <span>{icon.content}</span>;
}

const DEFAULT_EMAIL_FIELD_ORDER = 0;

type SubmitButtonProps = {
  fullWidth?: boolean;
  buttonWidth?: CSSProperties['width'];
  mobileButtonWidth?: CSSProperties['width'];
  isSubmitting?: boolean;
  buttonBorderColor?: CSSProperties['borderColor'];
  buttonBorderWidth?: CSSProperties['borderWidth'];
  buttonBorderStyle?: CSSProperties['borderStyle'];
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
  buttonBorderRadius?: CSSProperties['borderRadius'];
  mobileButtonBorderRadius?: CSSProperties['borderRadius'];
  mobileButtonFontSize?: CSSProperties['fontSize'];
  buttonFontSize?: CSSProperties['fontSize'];
  buttonIconSide?: 'left' | 'right';
  buttonIcon?: TIcon;
  buttonSubmittingText?: string;
  buttonText?: string;
  height?: number;
  buttonHoverBorderColor?: CSSProperties['borderColor'];
  buttonHoverBorderStyle?: CSSProperties['borderStyle'];
  buttonHoverBorderWidth?: CSSProperties['borderWidth'];
  buttonHoverBorderRadius?: CSSProperties['borderRadius'];
  buttonHoverCustomBackgroundColor?: string;
  buttonHoverCustomTextColor?: string;
  buttonHoverIconColor?: CSSProperties['color'];
  nodeId?: string;
};

function SubmitButton({
  fullWidth,
  buttonWidth,
  mobileButtonWidth,
  isSubmitting,
  buttonBorderColor,
  buttonBorderWidth,
  buttonBorderStyle,
  buttonBackgroundColor,
  buttonTextColor,
  buttonBorderRadius,
  mobileButtonBorderRadius,
  mobileButtonFontSize,
  buttonFontSize,
  buttonIconSide,
  buttonIcon,
  buttonSubmittingText,
  buttonText,
  height,
  buttonHoverBorderColor,
  buttonHoverBorderStyle,
  buttonHoverBorderWidth,
  buttonHoverBorderRadius,
  buttonHoverCustomBackgroundColor,
  buttonHoverCustomTextColor,
  buttonHoverIconColor,
  nodeId,
}: SubmitButtonProps) {
  const renderableButtonIcon = isRenderableSignupIcon(buttonIcon) ? buttonIcon : undefined;

  return (
    <button
      type="submit"
      data-node-id={nodeId}
      className={classnames(
        styles.button,
        isSubmitting && 'opacity-50',
        'flex items-center justify-center whitespace-nowrap',
      )}
      style={
        {
          '--button-background-color': buttonBackgroundColor,
          '--button-text-color': buttonTextColor,
          '--button-border-color': buttonBorderColor,
          '--button-border-width': buttonBorderWidth,
          '--button-border-style': buttonBorderStyle,
          boxSizing: 'border-box',
          height: height || '50px',
          padding: '0 16px 0 16px',
          flexShrink: 0,
          '--button-width': fullWidth ? '100%' : buttonWidth || 'auto',
          '--mobile-button-width': fullWidth ? '100%' : mobileButtonWidth || 'auto',
          '--mobile-button-font-size': mobileButtonFontSize || buttonFontSize,
          '--button-border-radius': buttonBorderRadius,
          '--mobile-button-border-radius': mobileButtonBorderRadius || buttonBorderRadius,
          ...(buttonHoverCustomBackgroundColor && {
            '--button-hover-background-color': buttonHoverCustomBackgroundColor,
          }),
          ...(buttonHoverCustomTextColor && { '--button-hover-text-color': buttonHoverCustomTextColor }),
          ...(buttonHoverBorderColor && { '--button-hover-border-color': buttonHoverBorderColor }),
          ...(buttonHoverBorderStyle && { '--button-hover-border-style': buttonHoverBorderStyle }),
          ...(buttonHoverBorderWidth && { '--button-hover-border-width': buttonHoverBorderWidth }),
          ...(buttonHoverBorderRadius &&
            buttonHoverBorderRadius !== 'inherit' && {
              '--button-hover-border-radius': buttonHoverBorderRadius,
            }),
          ...(buttonHoverIconColor && { '--button-hover-icon-color': buttonHoverIconColor }),
        } as CSSProperties
      }
    >
      {buttonIconSide === 'left' && renderableButtonIcon && (
        <div className={classnames(styles.buttonIcon, 'mr-2')}>{signupSubmitButtonIcon(renderableButtonIcon)}</div>
      )}
      {isSubmitting ? buttonSubmittingText : buttonText}
      {buttonIconSide === 'right' && renderableButtonIcon && (
        <div className={classnames(styles.buttonIcon, 'ml-2')}>{signupSubmitButtonIcon(renderableButtonIcon)}</div>
      )}
    </button>
  );
}

export function SignupElement(props: SignupElementProps) {
  const { element, children, attributes, className, isSubmitting, isLive, onEmailChange, gdprTranslations } = props;

  const {
    height,
    width,
    customAttributes,
    borderWidth,
    borderRadius,
    borderColor,
    borderStyle,
    padding,
    backgroundColor,
    gap,
    inputBorderWidth,
    inputBorderRadius,
    inputBorderColor,
    inputBackgroundColor,
    inputBorderStyle,
    inputRadioCheckboxAccentColor,
    inputTextColor,
    inputLabelColor,
    inputShowLabel,
    inputFontWeight,
    inputFontSize,
    inputEmailLabel,
    textareaHeight,
    mobileInputFontSize,
    mobileBorderRadius,
    inputPlaceholderColor,
    inputRadioCheckboxBackgroundColor,
    inputPlaceholder,
    buttonBorderWidth,
    buttonBorderRadius,
    buttonBorderColor,
    buttonBorderStyle,
    buttonBackgroundColor,
    buttonTextColor,
    buttonWidth,
    buttonText,
    buttonFontWeight,
    buttonFontSize,
    mobileButtonFontSize,
    mobileButtonBorderRadius,
    buttonSubmittingText,
    icon,
    iconColor,
    gdprTextColor,
    gdprCheckboxColor,
    orientation,
    mobileOrientation,
    inputFontFamily,
    buttonFontFamily,
    buttonIconSide,
    buttonIcon,
    buttonIconColor,
    buttonHoverBorderColor,
    buttonHoverBorderStyle,
    buttonHoverBorderWidth,
    buttonHoverBorderRadius,
    buttonHoverCustomBackgroundColor,
    buttonHoverCustomTextColor,
    buttonHoverIconColor,
    data,
    newsletterLists,
    emailFieldOrder,
  } = element.attrs || {};

  const hasTermsOfService = isLive ? props.hasTermsOfService : element.attrs.hasTermsOfService;
  const hasCustomTermsOfService = isLive ? props.hasCustomTermsOfService : element.attrs.hasCustomTermsOfService;
  const customTermsOfServiceText = isLive ? props.customTermsOfServiceText : element.attrs.customTermsOfServiceText;
  const hasAdditionalFields = data && data?.preferences?.length > 0;
  const hasNewsletterLists = newsletterLists && newsletterLists.length > 0;
  const hasMultipleNewsletterLists = newsletterLists && newsletterLists.length > 1;
  const preferences = data?.preferences || [];
  const emailPosition = emailFieldOrder ?? DEFAULT_EMAIL_FIELD_ORDER;
  const preferencesBeforeEmail = hasAdditionalFields ? preferences.slice(0, emailPosition) : [];
  const preferencesAfterEmail = hasAdditionalFields ? preferences.slice(emailPosition) : [];
  const hasBorder = inputBorderStyle !== 'none';

  const inputStyle = {
    borderColor: inputBorderColor,
    borderWidth: inputBorderWidth,
    borderRadius: inputBorderRadius,
    borderStyle: inputBorderStyle,
    height: height || '50px',
    color: inputTextColor,
    '--placeholder-color': inputPlaceholderColor,
    '--input-icon-color': iconColor,
  } as CSSProperties;

  const computedCheckboxRadius = useMemo(() => {
    return getCheckboxRadius(
      typeof inputBorderRadius === 'number' ? `${inputBorderRadius}px` : inputBorderRadius || '6px',
    );
  }, [inputBorderRadius]);

  const handleEmailChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    if (e.currentTarget.value) {
      onEmailChange?.(e.currentTarget.value);
    }
  }, []);

  const isStackedDesktop = hasAdditionalFields || (orientation || 'horizontal') === 'vertical';
  const isStackedMobile = hasAdditionalFields || (mobileOrientation || 'vertical') === 'vertical';

  const signupId = element.attrs?.id;
  const emailFieldNodeId = signupId ? `${signupId}__email` : undefined;

  const submitButtonProps = {
    nodeId: signupId ? `${signupId}__subscribe_button` : undefined,
    isSubmitting,
    buttonWidth: isStackedDesktop ? '100%' : buttonWidth,
    mobileButtonWidth: isStackedMobile ? '100%' : buttonWidth,
    buttonBorderColor,
    buttonBorderWidth,
    buttonBorderStyle,
    buttonBackgroundColor,
    buttonTextColor,
    buttonBorderRadius,
    mobileButtonBorderRadius,
    mobileButtonFontSize,
    buttonFontSize,
    buttonIconSide,
    buttonIcon,
    buttonSubmittingText,
    buttonText,
    height,
    buttonHoverBorderColor,
    buttonHoverBorderStyle,
    buttonHoverBorderWidth,
    buttonHoverBorderRadius,
    buttonHoverCustomBackgroundColor,
    buttonHoverCustomTextColor,
    buttonHoverIconColor,
  };

  return (
    <div
      {...customAttributes}
      className={classnames(
        className,
        styles.wrapper,
        customAttributes?.className,
        'flex flex-col gap-2',
        !isLive && 'pointer-events-none',
      )}
      style={
        {
          width: '100%',
          maxWidth: width,
          '--gap': gap,
          '--input-background-color': inputBackgroundColor,
          '--input-border-color': hasBorder ? inputBorderColor : 'transparent',
          '--input-checkbox-radius': computedCheckboxRadius,
          '--input-text-color': inputTextColor,
          '--input-label-color': inputLabelColor || inputTextColor,
          '--input-border-width': inputBorderWidth,
          '--input-border-radius': inputBorderRadius,
          '--input-radio-checkbox-accent-color': inputRadioCheckboxAccentColor,
          '--input-radio-checkbox-background-color': inputRadioCheckboxBackgroundColor,
          '--input-icon-color': iconColor,
          '--gdpr-text-color': gdprTextColor,
          '--gdpr-checkbox-color': gdprCheckboxColor,
          '--input-font-family': computeFont(inputFontFamily),
          '--button-font-family': computeFont(buttonFontFamily),
          '--button-icon-side': buttonIconSide,
          '--button-icon': buttonIcon,
          '--button-icon-color': buttonIconColor,
          '--input-font-weight': inputFontWeight,
          '--input-font-size': inputFontSize,
          '--mobile-input-font-size': mobileInputFontSize || inputFontSize,
          '--button-font-weight': buttonFontWeight,
          '--button-font-size': buttonFontSize,
          '--textarea-height': textareaHeight,
        } as CSSProperties
      }
      {...attributes}
    >
      <div
        className={classnames(
          styles.signupElement,
          'flex',
          hasAdditionalFields
            ? 'flex-col'
            : [
                styles.orientation[orientation || 'horizontal'],
                styles.mobileOrientation[mobileOrientation || 'vertical'],
              ],
          {
            'pointer-events-none': isSubmitting,
          },
        )}
        style={
          {
            borderColor: borderColor,
            borderWidth: borderWidth,
            borderStyle: borderStyle,
            padding: padding,
            backgroundColor: backgroundColor,
            width: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
            gap,
            '--border-radius': borderRadius,
            '--mobile-border-radius': mobileBorderRadius || borderRadius,
          } as CSSProperties
        }
        {...attributes}
      >
        {preferencesBeforeEmail.length > 0 && (
          <AdditionalFields data={preferencesBeforeEmail} style={inputStyle} isLabelVisible={inputShowLabel} />
        )}

        {hasAdditionalFields && inputShowLabel ? (
          <div className="flex flex-col gap-1 w-full">
            <label className={classnames(styles.inputLabelStyle, 'text-left w-full')} htmlFor={element.attrs.id}>
              {inputEmailLabel || 'Email'}
              <span>*</span>
            </label>
            <SignupInput
              icon={icon}
              inputPlaceholder={inputPlaceholder || 'Email'}
              handleEmailChange={handleEmailChange}
              inputStyle={inputStyle}
              nodeId={emailFieldNodeId}
            />
          </div>
        ) : (
          <SignupInput
            icon={icon}
            inputPlaceholder={inputPlaceholder || 'Email'}
            handleEmailChange={handleEmailChange}
            inputStyle={inputStyle}
            nodeId={emailFieldNodeId}
          />
        )}

        {preferencesAfterEmail.length > 0 && (
          <AdditionalFields data={preferencesAfterEmail} style={inputStyle} isLabelVisible={inputShowLabel} />
        )}

        {!hasMultipleNewsletterLists && <SubmitButton {...submitButtonProps} />}
      </div>

      {hasNewsletterLists && (
        <NewsletterListPicker newsletterLists={newsletterLists ?? []} gdprTranslations={gdprTranslations} />
      )}
      {hasMultipleNewsletterLists && <SubmitButton fullWidth {...submitButtonProps} />}

      {hasTermsOfService && (
        <SignupGDPRCompliance
          hasCustomTermsOfService={hasCustomTermsOfService}
          customTermsOfServiceText={customTermsOfServiceText}
          defaultTOSConsentValue={false}
          gdprTranslations={gdprTranslations}
        />
      )}
      {children}
    </div>
  );
}
